import { FC, useRef, useMemo, useState, useCallback } from 'react'
import CardFilterButton from 'src/components/CardFilterButton'
import { QuestionMarkTooltip } from 'src/components/QuestionMarkTooltip'
import { ScrollBarContainer } from 'src/components/ScrollBarContainer'
import { Skeleton } from 'src/components/Skeleton'
import {
  IndividualPost,
  IndividualPostsResponse,
} from 'src/services/__generated__/api'
import { PostCard } from './PostCard'
import { IndividualPostsModal } from './IndividualPostsModal'
import { PostDetailsModal } from './PostDetailsModal'
import {
  IndividualPostsFilterModal,
  RangeFilters,
} from './IndividualPostsFilterModal'
import styles from './individualPostsSection.module.scss'

const SENTIMENT_COLORS = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

const DEFAULT_RANGE_FILTERS: RangeFilters = {
  comments: { less: false, more: false, number: '' },
  reactions: { less: false, more: false, number: '' },
  reposts: { less: false, more: false, number: '' },
  shares: { less: false, more: false, number: '' },
  views: { less: false, more: false, number: '' },
}

const OPTIONAL_COUNTS = new Set(['reposts'])

const applyRangeFilters = (
  posts: IndividualPost[],
  rangeFilters: RangeFilters
): IndividualPost[] => {
  return posts.filter((post) => {
    for (const [key, filter] of Object.entries(rangeFilters)) {
      const value = post[key as keyof IndividualPost] as
        | number
        | null
        | undefined
      if (value === undefined) {
        if (
          OPTIONAL_COUNTS.has(key) &&
          filter.number &&
          (filter.more || filter.less)
        )
          return false
        continue
      }
      // An unknown count (views on Facebook / Instagram) can satisfy no range.
      if (value === null) {
        if (filter.number && (filter.more || filter.less)) return false
        continue
      }
      const threshold = parseFloat(filter.number)
      if (isNaN(threshold)) continue
      if (filter.more && value < threshold) return false
      if (filter.less && value > threshold) return false
    }
    return true
  })
}

// Real dimensions from PostCard/postCard.module.scss: .postCard.horizontal is a
// fixed 500px wide, radius 12, and its height is set by 16px padding + a ~36px
// header row + the 100px .imageWrapper content row + a ~42px metrics row -> 210.
// .postsContainer lays them out in a 16px-gap flex row, so mirroring the card
// box reserves the strip's real height and the section does not jump when the
// posts land.
const POST_CARD_WIDTH = 500
const POST_CARD_HEIGHT = 210
const POST_CARD_RADIUS = 12
// The strip scrolls horizontally; three cards is what fits the viewport before
// the fade, which is all the skeleton needs to cover.
const POST_SKELETON_COUNT = 3

interface IndividualPostsSectionProps {
  data: IndividualPostsResponse['data']
  isLoading?: boolean
}

export const IndividualPostsSection: FC<IndividualPostsSectionProps> = ({
  data,
  isLoading = false,
}) => {
  const [isExploreModalOpen, setIsExploreModalOpen] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [selectedPost, setSelectedPost] = useState<IndividualPost | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [filterOpenedFromExplore, setFilterOpenedFromExplore] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const posts = data?.posts || []

  // Filter state lifted up
  const platforms = useMemo(
    () =>
      Array.from(
        new Set(posts.map((p) => p.platform).filter(Boolean))
      ) as string[],
    [posts]
  )
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(
    () => new Set(['all', ...platforms])
  )
  const [rangeFilters, setRangeFilters] = useState<RangeFilters>(
    DEFAULT_RANGE_FILTERS
  )

  // Apply filters
  const filteredPosts = useMemo(() => {
    let result = posts

    // Platform filter (client-side since we get all posts)
    if (!selectedPlatforms.has('all')) {
      result = result.filter(
        (p) => p.platform && selectedPlatforms.has(p.platform)
      )
    }

    // Range filters
    result = applyRangeFilters(result, rangeFilters)

    // Sort newest → oldest
    result = [...result].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0
      const dateB = b.date ? new Date(b.date).getTime() : 0
      return dateB - dateA
    })

    return result
  }, [posts, selectedPlatforms, rangeFilters])

  const handleSaveFilters = useCallback(
    (newPlatforms: Set<string>, newRangeFilters: RangeFilters) => {
      setSelectedPlatforms(newPlatforms)
      setRangeFilters(newRangeFilters)
      setIsFilterModalOpen(false)
      if (filterOpenedFromExplore) {
        setIsExploreModalOpen(true)
        setFilterOpenedFromExplore(false)
      }
    },
    [filterOpenedFromExplore]
  )

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return
    setIsDragging(true)
    setStartX(e.pageX - scrollRef.current.offsetLeft)
    setScrollLeft(scrollRef.current.scrollLeft)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollRef.current.offsetLeft
    const walk = (x - startX) * 1.5
    scrollRef.current.scrollLeft = scrollLeft - walk
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  const handleViewDetails = (post: IndividualPost) => {
    setSelectedPost(post)
  }

  const handleCloseDetailsModal = () => {
    setSelectedPost(null)
  }

  const handleOpenExploreModal = () => {
    setIsExploreModalOpen(true)
  }

  const handleCloseExploreModal = () => {
    setIsExploreModalOpen(false)
  }

  return (
    <div className={styles.individualPostsSection}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <span className={styles.title}>Individual Posts</span>
          <QuestionMarkTooltip
            overlay="View and analyze individual posts from your tracked sources"
            placement="top"
          />
        </div>
        <div className={styles.actions}>
          <CardFilterButton onClick={() => setIsFilterModalOpen(true)} />
          <div className={styles.actionsDivider} />
          <button
            className={styles.exploreButton}
            onClick={handleOpenExploreModal}
            type="button"
          >
            Explore
          </button>
        </div>
      </div>

      <div className={styles.postsWrapper}>
        <ScrollBarContainer
          className={`${styles.postsContainer} ${isDragging ? styles.dragging : ''}`}
          isHorizontal
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          ref={scrollRef}
        >
          {isLoading &&
            Array.from({ length: POST_SKELETON_COUNT }).map((_, index) => (
              <Skeleton
                height={POST_CARD_HEIGHT}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                radius={POST_CARD_RADIUS}
                width={POST_CARD_WIDTH}
              />
            ))}
          {!isLoading &&
            filteredPosts.map((post) => (
              <PostCard
                key={post.id}
                onViewDetails={() => handleViewDetails(post)}
                post={post}
                variant="horizontal"
              />
            ))}
        </ScrollBarContainer>
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: SENTIMENT_COLORS.positive }}
          />
          <span className={styles.legendLabel}>Positive</span>
        </div>
        <div className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: SENTIMENT_COLORS.neutral }}
          />
          <span className={styles.legendLabel}>Neutral</span>
        </div>
        <div className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: SENTIMENT_COLORS.negative }}
          />
          <span className={styles.legendLabel}>Negative</span>
        </div>
      </div>

      <IndividualPostsModal
        data={filteredPosts}
        isOpen={isExploreModalOpen}
        onClose={handleCloseExploreModal}
        onOpenFilter={() => {
          setIsExploreModalOpen(false)
          setIsFilterModalOpen(true)
          setFilterOpenedFromExplore(true)
        }}
        onViewDetails={handleViewDetails}
      />

      {selectedPost && (
        <PostDetailsModal
          isOpen={!!selectedPost}
          onClose={handleCloseDetailsModal}
          post={selectedPost}
        />
      )}

      <IndividualPostsFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => {
          if (filterOpenedFromExplore) {
            setIsFilterModalOpen(false)
            setIsExploreModalOpen(true)
            setFilterOpenedFromExplore(false)
          } else {
            setIsFilterModalOpen(false)
          }
        }}
        onSave={handleSaveFilters}
        platformCounts={data?.platformCounts}
        platforms={platforms}
        rangeFilters={rangeFilters}
        selectedPlatforms={selectedPlatforms}
        totalPosts={posts.length}
      />
    </div>
  )
}
